import { createFileRoute, useLoaderData, useNavigate, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useEffect } from "react";
import { useClerk } from "@clerk/tanstack-react-start";
import { OnboardingLayout } from "@/components/onboarding-layout";
import { InvitationCodeCard } from "@/components/onboarding/InvitationCodeCard";
import { logError, logInfo } from "@/lib/structured-log";
import { redeemInvitationServer } from "@/server/onboarding";
import { extractApiErrorMessage } from "@/lib/api/error";

const PENDING_REGISTRATION_INVITE_KEY = "pending_registration_invite";

export const Route = createFileRoute("/onboarding/activation")({
  component: ActivationPage,
});

function ActivationPage() {
  const { t } = useTranslation(["pages"]);
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const parentData = useLoaderData({ from: "/onboarding" }) as
    | { onboardingStatus?: { onboarding_state?: string } }
    | undefined;
  const isJoinedWaitlist = parentData?.onboardingStatus?.onboarding_state === "waitlist_submitted";

  const navigate = useNavigate();
  const router = useRouter();
  const { signOut } = useClerk();

  const submitCode = useCallback(
    async (code: string) => {
      setError("");
      if (!code) return;

      setIsLoading(true);
      logInfo("onboarding.redeem_invitation.requested", {
        code_last4: code.slice(-4),
        code_length: code.length,
      });

      try {
        const status = await redeemInvitationServer({ data: { code } });
        logInfo("onboarding.redeem_invitation.succeeded", {
          onboarding_state: status.onboarding_state,
        });

        // Clear stored invite if successful
        localStorage.removeItem(PENDING_REGISTRATION_INVITE_KEY);

        // Trigger route guard to re-execute, which will automatically redirect
        // based on the new onboarding state via the unified state machine
        await router.invalidate();

        // Note: No manual redirect needed here. The route guard's state machine
        // will automatically determine the correct destination based on the new state.
        // This eliminates duplicate redirect logic and keeps all routing decisions
        // centralized in resolveRedirectTargetEnhanced.
      } catch (err) {
        logError("onboarding.redeem_invitation.failed", {
          code_last4: code.slice(-4),
          error: err instanceof Error ? err.message : "unknown-error",
        });
        setError(extractApiErrorMessage(err, t("pages:onboarding.activation.redeemFailed")));
        // Clear stored invite on error to prevent infinite loops on refresh
        localStorage.removeItem(PENDING_REGISTRATION_INVITE_KEY);
      } finally {
        setIsLoading(false);
      }
    },
    [router, t],
  );

  const handleInviteCodeSubmit = useCallback(() => {
    submitCode(inviteCode.trim());
  }, [inviteCode, submitCode]);

  // Auto-fill from localStorage
  useEffect(() => {
    const storedCode = localStorage.getItem(PENDING_REGISTRATION_INVITE_KEY);
    if (storedCode) {
      setInviteCode(storedCode);
    }
  }, []);

  const handleWaitlistClick = useCallback(() => {
    navigate({ to: "/onboarding/waitlist" });
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate({ to: "/signin" });
    } catch (error) {
      logError("auth.sign_out.failed", { error });
    }
  };

  return (
    <OnboardingLayout>
      <InvitationCodeCard
        inviteCode={inviteCode}
        onInviteCodeChange={setInviteCode}
        onSubmit={handleInviteCodeSubmit}
        onJoinWaitlist={handleWaitlistClick}
        onSignOut={handleSignOut}
        error={error}
        isLoading={isLoading}
        isJoinedWaitlist={isJoinedWaitlist}
      />
    </OnboardingLayout>
  );
}
